import React, { memo, MutableRefObject } from 'react';
import {
  StyledChartContainer,
  StyledChartLegendContainer,
  StyledChartTypeButtonsContainer,
  StyledChartView,
  StyledRightPanelContainer,
  StyledRightPanelTitle,
} from '../../Report.styles';
import ChartComponent, {
  ChartDataItem,
  MultipleChartDataItem,
} from '../../../../../components/Chart/Chart.component';
import ToggleButtonsComponent from '../../../../../components/ToggleButtons.component';
import { CHART_TYPE_OPTIONS } from '../../../../forms/Report/Params.form';
import DataUsageIcon from '../../../../../components/Icons/DataUsage.icon';
import BarChartIcon from '../../../../../components/Icons/BarChart.icon';
import HorizontalBarChartIcon from '../../../../../components/Icons/HorizontalBarChart.icon';
import { CHART_TYPE_ICON_SIZE } from '../../constants/constants';
import { useTranslation } from 'react-i18next';

interface ChartProps {
  chartType: string;
  chartContainerRef: MutableRefObject<HTMLDivElement | null>;
  chartData: ChartDataItem[];
  commonCount: number;
  stackBlockNames: string[] | undefined;
  handleChartClick: (item: ChartDataItem, key?: string | undefined) => void;
  handleMultipleChartClick: (
    data: [MultipleChartDataItem, MultipleChartDataItem],
  ) => void;
  chartLegendRef: MutableRefObject<HTMLDivElement | null>;
  updateChartType: (newString: string) => void;
  checkSubGroupSelected: () => boolean;
}

const Chart = ({
  chartType,
  chartContainerRef,
  chartData,
  commonCount,
  stackBlockNames,
  handleChartClick,
  handleMultipleChartClick,
  chartLegendRef,
  updateChartType,
  checkSubGroupSelected,
}: ChartProps) => {
  const { t } = useTranslation();

  return (
    <StyledChartView>
      <StyledChartContainer ref={chartContainerRef}>
        <ChartComponent
          type={chartType}
          data={chartData}
          commonCount={commonCount}
          stackBlockNames={stackBlockNames}
          onClick={handleChartClick}
          onMultipleClick={handleMultipleChartClick}
          legendPortalRef={chartLegendRef}
        />
      </StyledChartContainer>
      <StyledRightPanelContainer>
        {!!commonCount && (
          <StyledRightPanelTitle>{`${t(
            'Number of records',
          )}: ${commonCount}`}</StyledRightPanelTitle>
        )}
        <StyledRightPanelTitle>{t('Chart type')}</StyledRightPanelTitle>
        <StyledChartTypeButtonsContainer>
          <ToggleButtonsComponent
            active={CHART_TYPE_OPTIONS.map(({ value }) => value).indexOf(
              chartType,
            )}
            onSelect={index => updateChartType(CHART_TYPE_OPTIONS[index].value)}
          >
            <DataUsageIcon
              width={CHART_TYPE_ICON_SIZE}
              height={CHART_TYPE_ICON_SIZE}
              disabled={checkSubGroupSelected()}
            />
            <BarChartIcon
              width={CHART_TYPE_ICON_SIZE}
              height={CHART_TYPE_ICON_SIZE}
              disabled={checkSubGroupSelected()}
            />
            <HorizontalBarChartIcon
              width={CHART_TYPE_ICON_SIZE}
              height={CHART_TYPE_ICON_SIZE}
              disabled={!checkSubGroupSelected()}
            />
          </ToggleButtonsComponent>
        </StyledChartTypeButtonsContainer>
        <StyledRightPanelTitle>{t('Legend')}</StyledRightPanelTitle>
        <StyledChartLegendContainer ref={chartLegendRef} />
      </StyledRightPanelContainer>
    </StyledChartView>
  );
};

export const MemoizedChart = memo(Chart);
